"use client";

import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectSeparator,
	SelectTrigger,
	SelectValue,
} from "@ui/components/select";
import { cn } from "@ui/lib";
import { ORGANIZATION_MEMBER_ROLES } from "../hooks/member-roles";
import { useOrganizationRolesQuery } from "../hooks/use-roles";

/** Owner, Admin, Member plus any custom roles the retailer created. */
export function OrganizationRoleSelect({
	value,
	onSelect,
	disabled,
	organizationId,
	className,
	"aria-label": ariaLabel = "Role",
}: {
	value: string;
	onSelect: (value: string) => void;
	disabled?: boolean;
	organizationId: string;
	className?: string;
	"aria-label"?: string;
}) {
	const { data } = useOrganizationRolesQuery(organizationId);
	const customRoles = data?.roles?.map((role) => role.role) ?? [];
	const items: Record<string, string> = {
		...ORGANIZATION_MEMBER_ROLES,
		...Object.fromEntries(customRoles.map((role) => [role, role])),
	};

	return (
		<Select
			items={items}
			value={value}
			onValueChange={(next) => {
				if (typeof next === "string") {
					onSelect(next);
				}
			}}
			disabled={disabled ?? false}
		>
			<SelectTrigger
				aria-label={ariaLabel}
				className={cn("w-32", className)}
			>
				<SelectValue />
			</SelectTrigger>
			<SelectContent>
				<SelectGroup>
					{customRoles.length > 0 ? (
						<SelectLabel>Built in</SelectLabel>
					) : null}
					{Object.entries(ORGANIZATION_MEMBER_ROLES).map(
						([role, label]) => (
							<SelectItem key={role} value={role}>
								{label}
							</SelectItem>
						),
					)}
				</SelectGroup>
				{customRoles.length > 0 ? (
					<>
						<SelectSeparator />
						<SelectGroup>
							<SelectLabel>Custom</SelectLabel>
							{customRoles.map((role) => (
								<SelectItem key={role} value={role}>
									{role}
								</SelectItem>
							))}
						</SelectGroup>
					</>
				) : null}
			</SelectContent>
		</Select>
	);
}
